import { customTabKey, getCustomTabs } from './customTabs';

/*
 * Search and "where used" helpers shared by the scenario page and the account
 * structure page. Both pages use the same tab keys for the standard sections.
 */

export const SECTIONS = [
    { key: 'profitloss', label: 'Profit & Loss', tabKey: '1', rows: (s) => s?.profitloss },
    { key: 'assets', label: 'Balance Sheet — Assets', tabKey: '2', rows: (s) => s?.balancesheet?.assets },
    { key: 'liabilities', label: 'Balance Sheet — Liabilities', tabKey: '2', rows: (s) => s?.balancesheet?.liabilities },
    { key: 'equities', label: 'Balance Sheet — Equity', tabKey: '2', rows: (s) => s?.balancesheet?.equities },
    { key: 'cashflow', label: 'Cash Flow', tabKey: '3', rows: (s) => s?.cashflow },
    { key: 'drivers', label: 'Drivers', tabKey: '4', rows: (s) => s?.drivers },
];

const isSeparator = (row) => row?.row_type === 'separator' || row?.separator_id != null;

// Custom-tab rows wrap the real account in `row.account`.
export const unwrapAccount = (row) => row?.account ?? row;

/**
 * Stable identity for an account row, independent of which table renders it.
 * Associated (change-in) rows share account_id with their owner, so they get
 * their own namespace.
 */
export const accountIdentity = (row) => {
    if (!row || isSeparator(row)) return null;
    const acc = unwrapAccount(row);
    if (acc?.associated_account_id != null) return `assoc:${acc.associated_account_id}`;
    if (acc?.account_id != null) return `acct:${acc.account_id}`;
    return null;
};

/**
 * Every account in the standard sections, flattened in display order, with
 * the section it lives in and the names of its ancestors.
 */
export const listSearchableAccounts = (structure) => {
    const out = [];
    SECTIONS.forEach((section) => {
        const walk = (rows, path) => {
            (rows || []).forEach((row) => {
                if (isSeparator(row)) return;
                if (row.account_number != null) {
                    out.push({
                        identity: accountIdentity(row),
                        account: row,
                        section,
                        path,
                    });
                }
                if (row.children?.length) {
                    walk(row.children, [...path, `${row.account_number} ${row.name ?? ''}`.trim()]);
                }
            });
        };
        walk(section.rows(structure), []);
    });
    return out;
};

/**
 * Keys of the rows from the root down to the row with `identity`, or null
 * when the tree doesn't contain it. `getKey` maps a row to its table row key.
 */
export const findRowKeyPath = (rows, identity, getKey) => {
    for (const row of rows || []) {
        if (accountIdentity(row) === identity) return [getKey(row)];
        if (row.children?.length) {
            const sub = findRowKeyPath(row.children, identity, getKey);
            if (sub) return [getKey(row), ...sub];
        }
    }
    return null;
};

/**
 * Tab that should show `identity`. Stays on the current custom tab when it
 * contains the account, otherwise picks the account's home section.
 */
export const findTabKeyForAccount = (structure, identity, currentTabKey) => {
    const current = getCustomTabs(structure).find((t) => customTabKey(t) === currentTabKey);
    if (current && findRowKeyPath(current.custom_tab_accounts, identity, () => 0)) {
        return currentTabKey;
    }
    const section = SECTIONS.find((s) => findRowKeyPath(s.rows(structure), identity, () => 0));
    if (section) return section.tabKey;

    const custom = getCustomTabs(structure).find((t) =>
        findRowKeyPath(t.custom_tab_accounts, identity, () => 0),
    );
    return custom ? customTabKey(custom) : null;
};

// ─── Where used ──────────────────────────────────────────────────────────────

const ACCOUNT_TOKEN = /\{([^}]+)\}/g;

export const formulaReferences = (formula, accountNumber) => {
    if (typeof formula !== 'string' || accountNumber == null) return false;
    const target = String(accountNumber).trim();
    for (const match of formula.matchAll(ACCOUNT_TOKEN)) {
        if (match[1].trim() === target) return true;
    }
    return false;
};

// Collapse per-period formula rows into one entry per distinct formula text.
const groupFormulasByText = (formulas) => {
    const byText = new Map();
    formulas.forEach(({ formula, period }) => {
        const text = formula.trim();
        if (!byText.has(text)) byText.set(text, []);
        if (period != null) byText.get(text).push(String(period));
    });
    return Array.from(byText, ([formula, periods]) => ({ formula, periods }));
};

const toEntries = (values) => {
    if (!values) return [];
    if (typeof values.entries === 'function' && !Array.isArray(values)) return Array.from(values.entries());
    return Object.entries(values);
};

const parseWidgetTitle = (widget) => {
    try {
        const settings = typeof widget.settings === 'string' ? JSON.parse(widget.settings) : widget.settings;
        if (settings?.title) return settings.title;
    } catch {
        // Malformed settings — fall through to a generic label.
    }
    return `${widget.type ?? 'Widget'} #${widget.widget_id}`;
};

/**
 * Everything that points at `target` (an account row, possibly a custom-tab
 * wrapper). Each list is empty when there's nothing to report.
 *
 * @param {Object} args
 * @param {Object} args.structure         - state.accounts.accountStructure
 * @param {Object} args.target
 * @param {Array}  [args.allocations]     - state.allocations.allocations (camelCase)
 * @param {Array}  [args.widgets]         - state.reporting.widgets
 * @param {Object} [args.scenario]        - a scenario whose `values` hold cell formulas
 */
export const findAccountReferences = ({ structure, target, allocations, widgets, scenario }) => {
    const acc = unwrapAccount(target);
    const number = acc?.account_number != null ? String(acc.account_number).trim() : null;
    const isAssociated = acc?.associated_account_id != null;

    const entries = listSearchableAccounts(structure);
    const byNumber = new Map(entries.map((e) => [String(e.account.account_number).trim(), e]));

    const result = {
        formulas: [],
        scenarioFormulas: [],
        weights: [],
        parent: null,
        customTabs: [],
        allocations: [],
        widgets: [],
    };
    if (!acc || number == null) return result;

    // Structural formulas on other accounts.
    entries.forEach((entry) => {
        if (accountIdentity(entry.account) === accountIdentity(acc)) return;
        const hits = (entry.account.formulas || []).filter((f) => formulaReferences(f.formula, number));
        if (hits.length) result.formulas.push({ ...entry, formulas: groupFormulasByText(hits) });
    });

    // Cell formulas typed into the scenario that differ from the structural one.
    toEntries(scenario?.values).forEach(([ownerNumber, values]) => {
        const owner = byNumber.get(String(ownerNumber).trim());
        if (!Array.isArray(values)) return;
        const structural = new Map((owner?.account.formulas || []).map((f) => [f.period, (f.formula ?? '').trim()]));
        const hits = values.filter(
            (v) => formulaReferences(v?.formula, number) && v.formula.trim() !== structural.get(v.period),
        );
        if (hits.length) {
            result.scenarioFormulas.push({
                account: owner?.account ?? { account_number: ownerNumber },
                section: owner?.section,
                identity: owner?.identity,
                formulas: groupFormulasByText(hits),
            });
        }
    });

    // Weighted-average settings reference accounts by number.
    entries.forEach((entry) => {
        const a = entry.account;
        if (a.sum_type === 'weightedavgaccount' && String(a.weight ?? '').trim() === number) {
            result.weights.push({ ...entry, kind: 'Weighted average (periods)' });
        }
        if (a.bc_combine === 'weighted_avg' && String(a.bc_weight ?? '').trim() === number) {
            result.weights.push({ ...entry, kind: 'Weighted average (business case)' });
        }
    });

    // Parent the account rolls up into.
    if (acc.parent != null) {
        result.parent =
            entries.find((e) => !e.account.associated_account_id && e.account.account_id === acc.parent) ?? null;
    }

    // Favorite / custom tabs that show it.
    getCustomTabs(structure).forEach((tab) => {
        if (findRowKeyPath(tab.custom_tab_accounts, accountIdentity(acc), () => 0)) {
            result.customTabs.push({ name: tab.name, tabKey: customTabKey(tab) });
        }
    });

    // Allocations and reporting reference real accounts by id.
    if (!isAssociated) {
        (allocations || []).forEach((a) => {
            const isSource = Number(a.accountId) === acc.account_id;
            const isTarget = Number(a.targetAccountId) === acc.account_id;
            if (isSource || isTarget) {
                result.allocations.push({ allocation: a, role: isSource && isTarget ? 'Source & target' : isSource ? 'Source' : 'Target' });
            }
        });
    }

    (widgets || []).forEach((widget) => {
        const count = (widget.series || []).filter((s) =>
            isAssociated
                ? Number(s.associated_account_id) === acc.associated_account_id
                : Number(s.account_id) === acc.account_id &&
                  (s.associated_account_id == null || Number(s.associated_account_id) === -1),
        ).length;
        if (count) result.widgets.push({ widget, title: parseWidgetTitle(widget), count });
    });

    return result;
};

export const countReferences = (refs) =>
    refs.formulas.length +
    refs.scenarioFormulas.length +
    refs.weights.length +
    (refs.parent ? 1 : 0) +
    refs.customTabs.length +
    refs.allocations.length +
    refs.widgets.length;
